import moment from 'moment-timezone';

import { isElectionAddendumRequired } from '@mummssoftware/utils/utils';

type VisitId = string | number | null;

/**
 * EA state of the open visit, from data only (visit attributes, visit date,
 * workflow due-date response). Never depends on how the visit was opened.
 * - hidden: nothing owed and the visit has no EAC
 * - loading / error: due-date request in flight / failed (logged `[EA]`)
 * - beforeTrigger: visit day (site tz) is before the trigger day
 * - allowed: EA can be filled from this visit
 * - completed: the visit has EAC
 * - completedOwedLater: the visit has EAC and a new EA is owed from a
 *   trigger after the visit day (to be filled from a later visit)
 */
export type VisitElectionAddendumStatus =
  | 'hidden'
  | 'loading'
  | 'error'
  | 'beforeTrigger'
  | 'allowed'
  | 'completed'
  | 'completedOwedLater';

export type VisitElectionAddendumInput = {
  eaCreated: boolean;
  /** undefined while loading, null when the request failed. */
  requiredDate: mumms.electionAddendumRequiredDate | null | undefined;
  /** Visit start, UTC. */
  visitStartUtc: string | null | undefined;
  /** Used when the response has no timezone. */
  fallbackTimezone?: string | null;
};

// Same as utcToSiteMoment in @mummssoftware/utils/dates (not imported: that
// module pulls in formatters, which fails to load in isolated jest runs).
const toDay = (utc: string, timezone: string | null | undefined) =>
  timezone ? moment.utc(utc).tz(timezone) : moment.utc(utc);

/**
 * A live visit is dated today, so it never lands before the trigger; only a
 * past visit (reopened from the Visit Log) can.
 *
 * EAC: an owed response whose trigger is on/before the visit day is the EA
 * this visit completed (the reset hasn't reached the response yet), so it
 * stays `completed`. A failed request also stays `completed`: EAC is enough.
 */
export const getVisitElectionAddendumStatus = ({
  eaCreated,
  requiredDate,
  visitStartUtc,
  fallbackTimezone,
}: VisitElectionAddendumInput): VisitElectionAddendumStatus => {
  if (requiredDate === undefined) {
    return 'loading';
  }
  if (requiredDate === null || !visitStartUtc) {
    return eaCreated ? 'completed' : 'error';
  }
  const { daysRemaining, triggerDate, timezone } = requiredDate;
  if (!isElectionAddendumRequired(daysRemaining) || !triggerDate) {
    return eaCreated ? 'completed' : 'hidden';
  }
  const tz = timezone || fallbackTimezone;
  const beforeTrigger = toDay(visitStartUtc, tz).isBefore(
    toDay(triggerDate, tz),
    'day',
  );
  if (eaCreated) {
    return beforeTrigger ? 'completedOwedLater' : 'completed';
  }
  return beforeTrigger ? 'beforeTrigger' : 'allowed';
};

export type ElectionAddendumAutoOpenInput = {
  status: VisitElectionAddendumStatus;
  /** Visit dialog open, not minimized, not closing a prior visit. */
  visitDialogVisible: boolean;
  visitId: VisitId;
  /** Visit already has EAO (offered): EA opens from the chip only. */
  eaOffered: boolean;
  dismissedVisitId: VisitId;
};

/** When the EA prompt opens on its own (the EA chip can still open it). */
export const shouldAutoOpenElectionAddendum = ({
  status,
  visitDialogVisible,
  visitId,
  eaOffered,
  dismissedVisitId,
}: ElectionAddendumAutoOpenInput): boolean =>
  status === 'allowed' &&
  visitDialogVisible &&
  visitId != null &&
  !eaOffered &&
  dismissedVisitId !== visitId;
